import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Files, Upload, AlertCircle } from 'lucide-react';

/**
 * UsageChip — Iter-51.58.
 *
 * Compact "X/Y pages · U/L uploads left this month" chip driven by
 * `/api/me/usage`. Kept intentionally small so it can live above the
 * AddBook capture controls (so parents see remaining budget BEFORE
 * they tap Camera) and inside the Profile Guides & Info step 13
 * card without dominating the layout.
 *
 * Colour ramps by pressure:
 *   • pages < 80 % of cap  → neutral slate
 *   • pages 80-99 %        → amber warning
 *   • pages ≥ 100 %        → rose (blocked)
 *
 * Never shown for users without a session (usage endpoint requires
 * auth) — component simply renders null in that case.
 */
export default function UsageChip({ variant = 'inline', dataTestId = 'usage-chip' }) {
  const [u, setU] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/me/usage')
      .then(({ data }) => alive && setU(data))
      .catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, []);

  if (err || !u) return null;

  const pagesUsed  = u.active_pages_used || 0;
  const pagesLimit = u.active_pages_limit || 0;
  const uploadsUsed  = u.monthly_uploads_used || 0;
  const uploadsLimit = u.monthly_uploads_limit || 0;
  const uploadsLeft  = Math.max(0, uploadsLimit - uploadsUsed);
  const planName   = u.plan?.name || 'Starter';

  const ratio = pagesLimit > 0 ? pagesUsed / pagesLimit : 0;
  const tint = ratio >= 1
    ? 'bg-rose-100 text-rose-800 border-rose-200'
    : ratio >= 0.8
      ? 'bg-amber-100 text-amber-800 border-amber-200'
      : 'bg-slate-100 text-slate-700 border-slate-200';

  if (variant === 'card') {
    // Fuller card used inside Guides & Info step 13 — labelled sub-rows.
    return (
      <div data-testid={dataTestId}
        className={`rounded-2xl border-2 ${tint} p-4 space-y-2`}>
        <div className="text-[10px] font-extrabold uppercase tracking-wider opacity-70">
          On {planName}
        </div>
        <div className="flex items-center gap-2 text-sm font-bold">
          <Files size={15} strokeWidth={2.8}/>
          <span data-testid={`${dataTestId}-pages`}>{pagesUsed} / {pagesLimit} active pages used</span>
        </div>
        <div className="flex items-center gap-2 text-sm font-bold">
          <Upload size={15} strokeWidth={2.8}/>
          <span data-testid={`${dataTestId}-uploads`}>{uploadsLeft} / {uploadsLimit} uploads left this month</span>
        </div>
        {ratio >= 1 && (
          <div className="flex items-start gap-1.5 text-xs mt-1 pt-2 border-t border-current/20">
            <AlertCircle size={13} className="mt-0.5 shrink-0" strokeWidth={2.8}/>
            <span>Your plan's page limit is full. Delete some pages or upgrade your plan.</span>
          </div>
        )}
      </div>
    );
  }

  // Inline chip — one row, thin.
  return (
    <div data-testid={dataTestId}
      className={`inline-flex items-center gap-2 rounded-full border ${tint} px-3 py-1.5 text-[11px] font-bold`}>
      <Files size={12} strokeWidth={2.8}/>
      <span data-testid={`${dataTestId}-pages`}>{pagesUsed}/{pagesLimit} pages</span>
      <span className="opacity-40">·</span>
      <Upload size={12} strokeWidth={2.8}/>
      <span data-testid={`${dataTestId}-uploads`}>{uploadsLeft}/{uploadsLimit} uploads left</span>
    </div>
  );
}
